import { useImperativeHandle, useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { DateTime } from 'luxon';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledDateField } from '@/components/inputFields/ControlledDateField';
import { ControlledRadioField } from '@/components/inputFields/ControlledRadioField';
import { ControlledSelectField } from '@/components/inputFields/ControlledSelectField';
import { ControlledTextOrNumberField } from '@/components/inputFields/ControlledTextOrNumberField';
import { useClassInventoryProviderPair } from '@/hooks';
import { responsive } from '@/utils';

import { useLiveChartOptions, useSensorsAndProviders } from '../../hooks.ts';
import { ChartConfigProps, ChartType, HistogramChartConfigType, PlotType } from '../../types.ts';

export const HistogramChartConfig = ({
  onSave,
  config,
  id,
  ref,
  rootId,
}: ChartConfigProps<HistogramChartConfigType>) => {
  const { t } = useTranslation(['chartConfig']);
  const { classInventoryProviderPair } = useClassInventoryProviderPair();

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<HistogramChartConfigType>({
    defaultValues: {
      ...config,
      plotType: PlotType.HISTOGRAM,
    },
  });

  const selectedSensor = useWatch({ control, name: 'sensorId' });
  const selectedProvider = useWatch({ control, name: 'provider' });
  const selectedStartDate = useWatch({ control, name: 'start' });
  const selectedEndDate = useWatch({ control, name: 'end' });
  const chartType = useWatch({ control, name: 'type' });

  const triggerSave = async () => {
    if (!selectedStartDate) setValue('start', DateTime.now());
    if (!selectedEndDate) setValue('end', DateTime.now());
    const isValid = await trigger();
    if (isValid) onSave(id, getValues());
  };

  useImperativeHandle(ref, () => ({
    save: () => triggerSave().catch(err => console.error(err)),
  }));

  const { chartTypeOptions, chartIntervalOptions } = useLiveChartOptions();

  const selectedSensorIdArray = useMemo(
    () => (selectedSensor ? [selectedSensor] : undefined),
    [selectedSensor]
  );

  const selectedInventoryProviderArray = useMemo(
    () =>
      selectedProvider && classInventoryProviderPair[selectedProvider]
        ? [classInventoryProviderPair[selectedProvider]]
        : [],
    [classInventoryProviderPair, selectedProvider]
  );

  const { sensorOptions, providerOptions, isLoading } = useSensorsAndProviders(
    selectedSensorIdArray,
    selectedInventoryProviderArray,
    rootId
  );

  return (
    <FlexAligner direction={'column'} gap={responsive.spacing['5xlarge']}>
      <ControlledTextOrNumberField
        control={control}
        name={'title'}
        label={t('chartConfig:chartConfigModal.fields.title')}
      />

      <ControlledSelectField
        name={'sensorId'}
        control={control}
        label={t('chartConfig:chartConfigModal.fields.sensorToPlot')}
        placeholder={t('chartConfig:placeholder.sensor')}
        options={sensorOptions}
        rules={{
          required: t('chartConfig:histogram.isRequired.sensor'),
        }}
        caution={errors.sensorId?.message}
        isLoading={isLoading}
        isClearable={true}
      />
      <ControlledSelectField
        control={control}
        options={providerOptions}
        label={t('chartConfig:chartConfigModal.fields.typeToPlot')}
        placeholder={t('chartConfig:placeholder.type')}
        name={'provider'}
        isLoading={isLoading}
        rules={{ required: t('chartConfig:histogram.isRequired.type') }}
        caution={errors.provider?.message}
        isClearable={true}
      />
      <ControlledRadioField
        control={control}
        name={'type'}
        label={t('chartConfig:chartConfigModal.fields.chartType')}
        options={chartTypeOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.chartType'),
        }}
        caution={errors.type?.message}
      />
      <ControlledRadioField
        render={chartType === ChartType.LIVE}
        control={control}
        name={'interval'}
        label={t('chartConfig:chartConfigModal.fields.interval')}
        options={chartIntervalOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.interval'),
        }}
        caution={errors.interval?.message}
      />
      <ControlledDateField
        render={chartType === ChartType.HISTORICAL}
        control={control}
        name={'start'}
        label={t('chartConfig:chartConfigModal.fields.from')}
        rules={{
          required: t('chartConfig:histogram.isRequired.start'),
        }}
        caution={errors.start?.message}
      />
      <ControlledDateField
        render={chartType === ChartType.HISTORICAL}
        control={control}
        name={'end'}
        label={t('chartConfig:chartConfigModal.fields.to')}
        rules={{
          required: t('chartConfig:histogram.isRequired.end'),
        }}
        caution={errors.end?.message}
      />
    </FlexAligner>
  );
};
